// Generic legacy-parity report page: shared filter form (ReportFormComponent),
// KPI boxes from the summary endpoint, server-paginated / sortable table with
// footer sums (TableComponent + handleSummary), row colours and drill-down.
// Location-scoped: reloads when the session location changes.

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ArrowDown2, ArrowLeft2, ArrowRight2, Eye, Setting4 } from 'iconsax-reactjs';

import { adminApi, apiErrorMessage } from '../api/client';
import { ErrorBlock } from '../components/StateBlock';
import { formatDateTime, t } from '../i18n';
import { ensureLocation, onLocationChanged } from '../session/location';
import { RecordDetailDialog } from '../features/reports/RecordDetailDialog';
import { FilterFieldName, FilterValues, ReportFilterForm, serializeFilters } from '../features/reports/ReportFilterForm';
import { ReportColumn, ReportConfig, ReportKpi, renderCell, reportConfigByKey, reportConfigs } from '../features/reports/reportConfigs';
import { defaultRange, money, num, qty, toBackendDateTime, toInputValue } from '../features/reports/reportUtils';

export type { ReportConfig } from '../features/reports/reportConfigs';
export { reportConfigs, reportConfigByKey };

type Row = Record<string, unknown>;

const PAGE_SIZES = [10, 25, 50, 100];

function buildDefaults(config: ReportConfig): FilterValues {
  const values: FilterValues = {};
  const fields = config.filters ?? [];
  const primary = config.primaryRange ?? 'date';
  for (const name of fields) {
    if (name === primary) {
      const [start, end] = defaultRange();
      values[`${name}_start`] = toInputValue(start);
      values[`${name}_end`] = toInputValue(end);
    } else if (name === 'date' || name === 'created_at') {
      values[`${name}_start`] = '';
      values[`${name}_end`] = '';
    } else {
      values[name] = '';
    }
  }
  return values;
}

function sortKeyOf(column: ReportColumn): string | null {
  if (!column.sortable) return null;
  return typeof column.sortable === 'string' ? column.sortable : column.key;
}

function KpiBox({ kpi, data }: { kpi: ReportKpi; data: Row | null | undefined }) {
  const value = data?.[kpi.key];
  return (
    <Card>
      <CardContent sx={{ py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {kpi.format === 'count' ? qty(num(value)) : money(num(value))}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {t(kpi.label)}
        </Typography>
      </CardContent>
    </Card>
  );
}

const kpiGrid = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 } as const;

export function ReportViewPage({ config }: { config: ReportConfig }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const defaults = useMemo(() => buildDefaults(config), [config]);
  const urlKey = (config.urlParams ?? []).map((name) => `${name}=${searchParams.get(name) ?? ''}`).join('&');
  const urlPrefill = useMemo(() => {
    const prefill: FilterValues = {};
    for (const pair of urlKey ? urlKey.split('&') : []) {
      const [name, value] = pair.split('=');
      if (value) prefill[name] = value;
    }
    return prefill;
  }, [urlKey]);

  const [applied, setApplied] = useState<FilterValues>(() => ({ ...defaults, ...urlPrefill }));
  const [initialLabels, setInitialLabels] = useState<Record<string, string>>({});
  const [showForm, setShowForm] = useState(Boolean(Object.keys(urlPrefill).length));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState(config.defaultOrderBy ?? 'created_at desc');
  const [locationTick, setLocationTick] = useState(0);

  const [summary, setSummary] = useState<unknown>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [drillRow, setDrillRow] = useState<Row | null>(null);

  // Re-apply URL pre-fills when the query string changes (e.g. ?user_id=).
  useEffect(() => {
    setApplied({ ...defaults, ...urlPrefill });
    setPage(0);
  }, [defaults, urlPrefill]);

  useEffect(() => onLocationChanged(() => setLocationTick((n) => n + 1)), []);

  // Resolve the user name for a pre-filled ?user_id= (users page link).
  useEffect(() => {
    const id = urlPrefill.user_id;
    if (!id) return;
    let alive = true;
    adminApi
      .get(`users/${id}`)
      .then((r) => {
        const name = (r.data as Row | undefined)?.name;
        if (alive && name) setInitialLabels((l) => ({ ...l, user_id: String(name) }));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [urlPrefill]);

  const filterParams = useMemo(
    () =>
      serializeFilters(
        (config.filters ?? []) as FilterFieldName[],
        applied,
        { primaryRange: config.primaryRange ?? 'date', secondaryCreatedAt: config.secondaryCreatedAt },
        toBackendDateTime
      ),
    [config, applied]
  );

  // Summary (KPIs)
  useEffect(() => {
    if (!config.summaryEndpoint) return;
    let alive = true;
    (async () => {
      try {
        await ensureLocation().catch(() => undefined);
        const res = await adminApi.get(config.summaryEndpoint!, { params: filterParams });
        if (!alive) return;
        const body = res.data as Row;
        setSummary(config.summaryKey ? body?.[config.summaryKey] : body);
      } catch (err) {
        if (alive) setError(apiErrorMessage(err, t('report.unableToLoad')));
      }
    })();
    return () => {
      alive = false;
    };
  }, [config, filterParams, locationTick]);

  // Table
  const loadTable = useCallback(async () => {
    if (!config.tableEndpoint) return;
    setLoading(true);
    setError(null);
    try {
      await ensureLocation().catch(() => undefined);
      const res = await adminApi.get(config.tableEndpoint, {
        params: { ...filterParams, page: page + 1, limit: rowsPerPage, orderBy }
      });
      const body = res.data as Row;
      const table = (body?.table && typeof body.table === 'object' ? body.table : body) as Row;
      const data = Array.isArray(table?.data) ? (table.data as Row[]) : Array.isArray(body) ? (body as unknown as Row[]) : [];
      setRows(data);
      setCount(Number(table?.count ?? data.length));
    } catch (err) {
      setRows([]);
      setCount(0);
      setError(apiErrorMessage(err, t('report.unableToLoad')));
    } finally {
      setLoading(false);
    }
  }, [config, filterParams, page, rowsPerPage, orderBy]);

  useEffect(() => {
    void loadTable();
  }, [loadTable, locationTick]);

  const columns = useMemo(() => config.columns ?? [], [config]);
  const hasSums = columns.some((c) => c.sum);
  const sums = useMemo(() => {
    const totals: Record<string, number> = {};
    if (!hasSums) return totals;
    const included = rows.filter((row) => !config.sumExclude?.(row));
    for (const column of columns) {
      if (!column.sum) continue;
      totals[column.key] = included.reduce((acc, row) => acc + (column.value ? column.value(row) : num(row[column.key])), 0);
    }
    return totals;
  }, [rows, columns, hasSums, config]);

  const [currentSortKey, currentSortDir] = orderBy.split(',')[0].split(' ');
  const toggleSort = (key: string) => {
    const dir = currentSortKey === key && currentSortDir !== 'asc' ? 'asc' : 'desc';
    setOrderBy(`${key} ${dir}`);
    setPage(0);
  };

  const primary = config.primaryRange ?? 'date';
  const rangeStart = applied[`${primary}_start`];
  const rangeEnd = applied[`${primary}_end`];
  const showRange = (config.filters ?? []).includes(primary) && rangeStart && rangeEnd;
  const extraCols = (config.expand ? 1 : 0) + (config.drilldown ? 1 : 0);

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" onClick={() => navigate('/reports')} startIcon={<ArrowLeft2 size={16} />} sx={{ minWidth: 'auto', px: 1 }}>
            {t('nav.reports')}
          </Button>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t(config.title)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {showRange ? `${formatDateTime(rangeStart)} – ${formatDateTime(rangeEnd)}` : t(config.description)}
            </Typography>
          </Box>
        </Stack>
        {config.filters?.length ? (
          <Button size="small" variant={showForm ? 'contained' : 'outlined'} startIcon={<Setting4 size={16} />} onClick={() => setShowForm((s) => !s)}>
            {t('report.toggleForm')}
          </Button>
        ) : null}
      </Stack>

      {config.filters?.length ? (
        <Collapse in={showForm}>
          <Card>
            <CardContent>
              <ReportFilterForm
                fields={config.filters}
                values={applied}
                defaults={defaults}
                statusOptions={config.statusOptions}
                typeOptions={config.typeOptions}
                initialLabels={initialLabels}
                onSubmit={(values) => {
                  setApplied({ ...values });
                  setPage(0);
                }}
              />
            </CardContent>
          </Card>
        </Collapse>
      ) : null}

      {error ? <Alert severity="error">{error}</Alert> : null}

      {config.kpis?.length ? (
        <Box sx={kpiGrid}>
          {config.kpis.map((kpi) => (
            <KpiBox key={kpi.key} kpi={kpi} data={summary as Row | null} />
          ))}
        </Box>
      ) : null}

      {config.kpiGroups?.map((group) => (
        <Card key={group.title}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              {t(group.title)}
            </Typography>
            <Box sx={kpiGrid}>
              {group.kpis.map((kpi) => (
                <KpiBox key={kpi.key} kpi={kpi} data={Array.isArray(summary) ? (summary[group.index] as Row | undefined) : null} />
              ))}
            </Box>
          </CardContent>
        </Card>
      ))}

      {columns.length ? (
        <Card>
          {loading ? <LinearProgress /> : <Box sx={{ height: 4 }} />}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {config.expand ? <TableCell sx={{ width: 40 }} /> : null}
                  {columns.map((column) => {
                    const sortKey = sortKeyOf(column);
                    return (
                      <TableCell key={column.key} align={column.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: column.minWidth }}>
                        {sortKey ? (
                          <TableSortLabel
                            active={currentSortKey === sortKey}
                            direction={currentSortKey === sortKey && currentSortDir === 'asc' ? 'asc' : 'desc'}
                            onClick={() => toggleSort(sortKey)}
                          >
                            {t(column.label)}
                          </TableSortLabel>
                        ) : (
                          t(column.label)
                        )}
                      </TableCell>
                    );
                  })}
                  {config.drilldown ? <TableCell sx={{ width: 48 }} /> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + extraCols} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">{t('report.noData')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, index) => {
                    const id = String(row.id ?? index);
                    const tone = config.rowTone?.(row);
                    const isOpen = expanded.has(id);
                    return (
                      <Fragment key={id}>
                        <TableRow
                          hover
                          onDoubleClick={config.drilldown ? () => setDrillRow(row) : undefined}
                          sx={{
                            cursor: config.drilldown ? 'pointer' : undefined,
                            bgcolor: tone ? (theme) => alpha(theme.palette[tone].main, 0.08) : undefined
                          }}
                        >
                          {config.expand ? (
                            <TableCell sx={{ width: 40 }}>
                              <IconButton
                                size="small"
                                aria-label={t('report.expand')}
                                onClick={() =>
                                  setExpanded((current) => {
                                    const next = new Set(current);
                                    if (next.has(id)) next.delete(id);
                                    else next.add(id);
                                    return next;
                                  })
                                }
                              >
                                {isOpen ? <ArrowDown2 size={16} /> : <ArrowRight2 size={16} />}
                              </IconButton>
                            </TableCell>
                          ) : null}
                          {columns.map((column) => (
                            <TableCell key={column.key} align={column.align} sx={{ whiteSpace: column.minWidth ? 'normal' : 'nowrap', minWidth: column.minWidth }}>
                              {renderCell(column, row)}
                            </TableCell>
                          ))}
                          {config.drilldown ? (
                            <TableCell sx={{ width: 48 }}>
                              <Tooltip title={t('action.view')}>
                                <IconButton size="small" onClick={() => setDrillRow(row)}>
                                  <Eye size={18} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          ) : null}
                        </TableRow>
                        {config.expand && isOpen ? (
                          <TableRow>
                            <TableCell colSpan={columns.length + extraCols} sx={{ bgcolor: 'action.hover' }}>
                              {config.expand(row)}
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
              {hasSums && rows.length ? (
                <TableFooter>
                  <TableRow>
                    {config.expand ? <TableCell /> : null}
                    {columns.map((column, i) => (
                      <TableCell key={column.key} align={column.align} sx={{ fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap' }}>
                        {column.sum ? (column.format === 'qty' ? qty(sums[column.key]) : money(sums[column.key])) : i === 0 ? t('report.pageTotal') : ''}
                      </TableCell>
                    ))}
                    {config.drilldown ? <TableCell /> : null}
                  </TableRow>
                </TableFooter>
              ) : null}
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={count}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={PAGE_SIZES}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
            labelRowsPerPage={t('report.rowsPerPage')}
            labelDisplayedRows={({ from, to, count: total }) => t('report.displayedRows', { from, to, count: total })}
          />
        </Card>
      ) : null}

      <RecordDetailDialog kind={drillRow ? config.drilldown ?? null : null} row={drillRow} onClose={() => setDrillRow(null)} />
    </Stack>
  );
}

// Route wrapper — reads the :reportKey param (or a forced key) and resolves the config.
// Legacy paths (/reports/sales, /reports/time_clock, /reports/items/top …) resolve via aliases.
export function ReportViewRoute({ reportKey: forcedKey }: { reportKey?: string } = {}) {
  const params = useParams<{ reportKey: string }>();
  const key = forcedKey ?? params.reportKey ?? '';
  const config = reportConfigByKey(key);
  const [searchParams] = useSearchParams();
  if (!config) return <ErrorBlock message={t('report.unknown', { key })} />;
  if (config.redirectTo) {
    const qs = searchParams.toString();
    return <Navigate to={`${config.redirectTo}${qs ? `?${qs}` : ''}`} replace />;
  }
  return <ReportViewPage key={config.key} config={config} />;
}
